import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import MovieCard from '@/components/base/MovieCard';
import FilterPanel from '@/pages/movies/components/FilterPanel';
import { filterMovies } from '@/lib/catalog';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const PAGE_SIZE = 8;

type SortKey = 'latest' | 'rating' | 'popular';

const sortLabels: Record<SortKey, string> = {
  latest: '最新上映',
  rating: '评分从高到低',
  popular: '热门程度',
};

export default function MoviesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const genreParam = searchParams.get('genre') ?? '';
  const sortParam = searchParams.get('sort') ?? '';

  const [selectedGenres, setSelectedGenres] = useState<string[]>(genreParam ? [genreParam] : []);
  const [year, setYear] = useState('全部年份');
  const [region, setRegion] = useState('全部地区');
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState<SortKey>(
    sortParam === 'rating' || sortParam === 'popular' ? sortParam : 'latest',
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  useDocumentMeta({
    title: '午夜电影网 - 最新电影分类浏览',
    description:
      '按类型、年份、地区和评分筛选高清电影，支持最新上映、评分高低与热门程度排序，快速找到今晚想看的电影。',
    keywords: '电影分类,高清电影,电影筛选,最新电影,午夜电影网',
  });

  useEffect(() => {
    if (genreParam) {
      setSelectedGenres([genreParam]);
      setVisibleCount(PAGE_SIZE);
    }
  }, [genreParam]);

  useEffect(() => {
    if (sortParam === 'rating' || sortParam === 'popular') {
      setSort(sortParam);
    }
  }, [sortParam]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  const filtered = useMemo(
    () => filterMovies({ genres: selectedGenres, year, region, minRating, sort }),
    [selectedGenres, year, region, minRating, sort],
  );

  const visibleMovies = filtered.slice(0, visibleCount);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(totalPages, Math.ceil(visibleCount / PAGE_SIZE));

  const resetPaging = () => {
    setVisibleCount(PAGE_SIZE);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleGenre = (genre: string) => {
    const next = selectedGenres.includes(genre)
      ? selectedGenres.filter((item) => item !== genre)
      : [...selectedGenres, genre];
    setSelectedGenres(next);
    setSearchParams(next.length === 1 ? { genre: next[0] } : {}, { replace: true });
    resetPaging();
  };

  const resetAll = () => {
    setSelectedGenres([]);
    setYear('全部年份');
    setRegion('全部地区');
    setMinRating(0);
    setSort('latest');
    setSearchParams({}, { replace: true });
    resetPaging();
  };

  const goToPage = (page: number) => {
    setVisibleCount(page * PAGE_SIZE);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const activeCount =
    selectedGenres.length +
    (year !== '全部年份' ? 1 : 0) +
    (region !== '全部地区' ? 1 : 0) +
    (minRating > 0 ? 1 : 0);

  const filterProps = {
    selectedGenres,
    year,
    region,
    minRating,
    onToggleGenre: toggleGenre,
    onYearChange: (value: string) => {
      setYear(value);
      resetPaging();
    },
    onRegionChange: (value: string) => {
      setRegion(value);
      resetPaging();
    },
    onRatingChange: (value: number) => {
      setMinRating(value);
      resetPaging();
    },
    onReset: resetAll,
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-10 pt-24 md:pb-14 md:pt-32">
        <div className="absolute -right-24 top-10 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影分类' }]} />
          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="eyebrow text-[11px] text-primary-500">Movie Library</p>
              <h1 className="mt-2 font-heading text-3xl font-semibold tracking-wide text-foreground-950 md:text-5xl">
                电影分类浏览
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-500">
                从类型、年份、地区到评分，用结构化筛选把海量片单收敛成今晚的一个选择。所有影片资料均为演示内容。
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-film-line text-primary-500" aria-hidden="true"></i>
                共 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 部影片
              </span>
              <span className="h-3 w-px bg-background-400/50" />
              <span className="flex items-center gap-1.5">
                <i className="ri-filter-3-line text-primary-500" aria-hidden="true"></i>
                {activeCount} 个筛选条件
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="shell py-10 md:py-14" ref={listRef}>
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-lg border border-background-200 bg-background-100 p-5">
              <FilterPanel {...filterProps} />
            </div>
          </aside>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-500/70 hover:text-primary-500 lg:hidden"
                >
                  <i className="ri-equalizer-line" aria-hidden="true"></i>
                  筛选
                  {activeCount > 0 && (
                    <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-background-50">
                      {activeCount}
                    </span>
                  )}
                </button>
                <p className="text-xs text-foreground-500">
                  第 {currentPage} / {totalPages} 页 · 显示 {visibleMovies.length} 部
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="hidden text-xs text-foreground-500 sm:inline">排序</span>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(event) => {
                      setSort(event.target.value as SortKey);
                      resetPaging();
                    }}
                    aria-label="排序方式"
                    className="appearance-none rounded-md border border-background-300/70 bg-background-50 py-1.5 pl-3 pr-8 text-xs text-foreground-800 focus:border-primary-500/70 focus:outline-none"
                  >
                    {Object.entries(sortLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <i
                    className="ri-arrow-down-s-line pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-foreground-500"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>

            {(selectedGenres.length > 0 || activeCount > 0) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {selectedGenres.map((genre) => (
                  <button
                    key={genre}
                    type="button"
                    onClick={() => toggleGenre(genre)}
                    className="flex items-center gap-1 whitespace-nowrap rounded-full border border-primary-500/50 bg-primary-500/10 px-3 py-1 text-[11px] text-primary-500"
                  >
                    {genre}
                    <i className="ri-close-line" aria-hidden="true"></i>
                  </button>
                ))}
                {year !== '全部年份' && (
                  <span className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600">
                    {year}
                  </span>
                )}
                {region !== '全部地区' && (
                  <span className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600">
                    {region}
                  </span>
                )}
                {minRating > 0 && (
                  <span className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600">
                    {minRating.toFixed(1)} 分以上
                  </span>
                )}
                <button
                  type="button"
                  onClick={resetAll}
                  className="whitespace-nowrap text-[11px] text-foreground-500 underline-offset-4 transition-colors hover:text-primary-500 hover:underline"
                >
                  清除全部
                </button>
              </div>
            )}

            <div className="mt-6">
              {visibleMovies.length === 0 ? (
                <EmptyState
                  icon="ri-movie-2-line"
                  title="暂无符合条件的电影"
                  description="试着放宽年份或评分条件，或者清除部分类型筛选，片库里还有很多值得一看的作品。"
                  onReset={resetAll}
                  actionLabel="回到首页"
                  actionTo="/"
                />
              ) : (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
                  {visibleMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} showGenres />
                  ))}
                </div>
              )}
            </div>

            {visibleMovies.length > 0 && (
              <div className="mt-10 flex flex-col items-center gap-5">
                {visibleCount < filtered.length && (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + 4)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300/70 px-5 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-500/70 hover:text-primary-500"
                  >
                    <i className="ri-add-line" aria-hidden="true"></i>
                    加载更多（剩余 {filtered.length - visibleCount} 部）
                  </button>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => goToPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => goToPage(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                      className={`h-9 w-9 rounded-md text-xs transition-colors ${
                        page === currentPage
                          ? 'bg-primary-500 font-medium text-background-50'
                          : 'border border-background-300/70 text-foreground-600 hover:border-primary-500/70 hover:text-primary-500'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-50/70 backdrop-blur-sm"
          />
          <div className="absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col bg-background-100">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <p className="text-sm font-medium text-foreground-900">筛选电影</p>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭筛选面板"
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 hover:text-foreground-900"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <FilterPanel {...filterProps} />
            </div>
            <div className="border-t border-background-200 p-4">
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-full rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
              >
                查看 {filtered.length} 部影片
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}